import React, { useEffect, useMemo, useState } from "react";
import usePlan from "@/hooks/usePlan";
import { toast } from "react-toastify";
import AgentBioHeader from "@/components/AgentBioHeader";
import AgentBioSections from "@/components/AgentBioSections";
import UnderlineTabs from "@/components/UnderlineTabs";
import ProfileImageCropper from "@/components/ProfileImageCropper";
import PremiumPlanModal from "@/components/PlanUpgrade/PremiumPlanModal";
import { useSaveBioProfile } from "@/hooks/useSaveBioProfile";
import { useAgentPublicProfile } from "@/hooks/useAgentPublicProfile";
import SortableBlocks from "./SortableBlocks";
import ScaledPreview from "@/components/ScaledPreview";
import { daysLeft, formatDay } from "@/components/GitchaProWindow";
import { useGitchaProOffer } from "@/hooks/useGitchaPro";
import { gitchaBioUrl } from "@/hooks/useAgentBioLink";
import {
  BIO_LIMIT,
  MAX_TESTIMONIALS,
  MISSING_DETAIL_LABELS,
  bioProgress,
  detailRows,
  draftErrors,
  draftFrom,
  isDirty,
  midSentence,
  missingDetails,
  payloadFrom,
  previewAgent,
} from "./bioStudioModel";
import { BioLegalLinks } from "@/components/BioDisclosure";
import GitchaLogo from "@/images/gitcha-logo.svg";
import { useAppConfig } from "@/appConfig";
import { tenantShortName } from "@/helpers/tenant";
import { marketsFromPlaces } from "@/components/DirectoryMarketCards";
import { bioPlaces } from "@/components/AgentBioMarkets";
import "./BioStudio.scss";

const SECTION_LABELS = {
  buyers: "Your buyers",
  history: "Past Buyer Want-Listings",
  markets: "Where you work",
  links: "Website, socials and video",
  testimonials: "Client testimonials",
};

const listOf = (items) =>
  items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

const readImage = (file, onLoad) => {
  if (!file || !/^image\//.test(file.type)) return false;
  const reader = new FileReader();
  reader.onload = () => onLoad(reader.result);
  reader.readAsDataURL(file);
  return true;
};

const Field = ({ label, hint, error, children }) => (
  <label className="bio-studio__field">
    <span className="bio-studio__label">{label}</span>
    {children}
    {error ? <span className="bio-studio__error">{error}</span> : hint ? <span className="bio-studio__hint">{hint}</span> : null}
  </label>
);

const Row = ({ title, aside, note, className, children }) => (
  <section className={`bio-studio__row${className ? ` ${className}` : ""}`}>
    <div className="bio-studio__row-head">
      <h3>{title}</h3>
      {note ? <p className="bio-studio__row-note">{note}</p> : null}
      {aside}
    </div>
    <div className="bio-studio__row-body">{children}</div>
  </section>
);

const DetailsRow = ({ userDetails, onEdit, roster, missing }) => (
  <Row
    title="Your details"
    className="bio-studio__details"
    aside={
      <button type="button" className="bio-studio__link" onClick={onEdit}>
        Edit details
      </button>
    }
  >
    <dl>
      {detailRows(userDetails, roster).map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd className={value ? "" : "is-empty"}>{value || "Not added"}</dd>
        </div>
      ))}
    </dl>
    {missing.length ? (
      <p className="bio-studio__missing">
        Add your {listOf(missing.map((key) => MISSING_DETAIL_LABELS[key]))} so your profile can be&nbsp;saved.{" "}
        <button type="button" className="bio-studio__link" onClick={onEdit}>
          Add details
        </button>
      </p>
    ) : null}
    {roster?.linked && (roster.brokerage || roster.license) ? (
      <p className="bio-studio__details-note">
        Brokerage and license come from your {roster.network || "MLS"} membership and stay current on their&nbsp;own.
      </p>
    ) : null}
  </Row>
);

const trialLeftLine = (trialEndsOn) => {
  const left = daysLeft(trialEndsOn);
  return `${left} ${left === 1 ? "day" : "days"} left in your Gitcha Pro trial`;
};

const BioStudio = ({ userDetails, isBlsAgent, onEditDetails, updateReloadParam, roster }) => {
  const { isPro, loaded: planKnown } = usePlan();
  const networkName = tenantShortName(useAppConfig()?.tenant);
  const [draft, setDraft] = useState(() => draftFrom(userDetails));
  const [previewPro, setPreviewPro] = useState(false);
  const [cropper, setCropper] = useState(null);
  const [planOpen, setPlanOpen] = useState(false);
  const [brokenImages, setBrokenImages] = useState([]);
  const save = useSaveBioProfile({
    onSuccess: () => toast.success("Your bio is updated."),
    onError: (error) => toast.error(error.message),
  });

  useEffect(() => {
    setDraft(draftFrom(userDetails));
  }, [userDetails]);

  const set = (key) => (event) => setDraft({ ...draft, [key]: event.target.value });
  const setTestimonial = (index, value) =>
    setDraft({ ...draft, testimonials: draft.testimonials.map((item, i) => (i === index ? value : item)) });
  const removeTestimonial = (index) => {
    const rest = draft.testimonials.filter((_, i) => i !== index);
    setDraft({ ...draft, testimonials: rest.length ? rest : [""] });
  };

  const errors = draftErrors(draft);
  const missing = missingDetails(userDetails);
  const progress = bioProgress(draft);
  const dirty = isDirty(draft, userDetails);
  const canSave = dirty && !Object.keys(errors).length && !missing.length && !save.isPending;
  const canUpgrade = planKnown && !isPro && isBlsAgent;
  const showPro = isPro || (canUpgrade && previewPro);
  const profile = useAgentPublicProfile(userDetails?.slug).data;
  const agent = useMemo(
    () => previewAgent(userDetails, draft, showPro, profile),
    [userDetails, draft, showPro, profile],
  );
  const hasContent = {
    buyers: (profile?.activeWantAds || []).length > 0,
    history: (profile?.history?.past || []).length > 0,
    markets: marketsFromPlaces(bioPlaces(profile)).length > 0,
    links: [draft.websiteUrl, draft.facebookUrl, draft.instagramUrl, draft.youtubeUrl].some((value) => String(value || "").trim()),
    testimonials: draft.testimonials.some((value) => String(value || "").trim()),
  };
  const setLayout = (bioLayout) => setDraft({ ...draft, bioLayout });
  const buyerCount = (profile?.activeWantAds || []).length;
  const pastCount = (profile?.history?.past || []).length;
  const sectionStatus = (key) => {
    if (key === "buyers")
      return buyerCount
        ? `Shows your ${buyerCount} active Buyer Want-${buyerCount === 1 ? "Listing" : "Listings"} automatically.`
        : "Appears once you post a Buyer Want-Listing.";
    if (key === "history")
      return pastCount
        ? `Shows your ${pastCount} past Buyer Want-${pastCount === 1 ? "Listing" : "Listings"} automatically.`
        : "Appears once a Buyer Want-Listing closes or ends.";
    if (key === "markets")
      return hasContent.markets
        ? "Shows the cities from your Buyer Want-Listings automatically."
        : "Appears once you post a Buyer Want-Listing.";
    if (key === "links")
      return hasContent.links ? "Showing on your page." : "Add a link or a video to show this section.";
    return hasContent.testimonials ? "Showing on your page. The first one shows large." : "Add a testimonial to show this section.";
  };
  const shows = (url) => Boolean(url) && !brokenImages.includes(url);
  const markBroken = (url) => () => setBrokenImages((list) => (list.includes(url) ? list : [...list, url]));
  const offer = useGitchaProOffer(Boolean(isBlsAgent)).data;
  const locked = !isPro;

  const pickImage = (isSquare) => (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = null;
    if (!readImage(file, (image) => setCropper({ image, isSquare }))) {
      toast.error("Choose a JPG or PNG image.");
    }
  };

  return (
    <div className="bio-studio">
      <div className="bio-studio__editor">
        <div className="bio-studio__intro">
          <img className="bio-studio__brand" src={GitchaLogo} alt="Gitcha" />
          <div className="bio-studio__intro-text">
            <h2 className="bio-studio__title">Your profile</h2>
            <p className="bio-studio__lede">
              {isBlsAgent
                ? `One profile for your Buyer Want-Listings in the ${networkName} Buyer Listing Service and your agent page on\u00a0Gitcha.com.`
                : "One profile for your Buyer Want-Listings and your agent page on\u00a0Gitcha.com."}
            </p>
          </div>
          {userDetails?.slug ? (
            <a className="bio-studio__view" href={gitchaBioUrl(userDetails.slug)} target="_blank" rel="noopener noreferrer">
              View on Gitcha.com
            </a>
          ) : null}
        </div>

        {canUpgrade ? (
          <section className="bio-studio__upgrade">
            <div>
              <h3>Your bio is on the free plan</h3>
              <p>
                Pro adds your photo, logo, bio, client testimonials, links and a form buyers fill out to reach you.
              </p>
            </div>
            <button type="button" className="bio-studio__primary" onClick={() => setPlanOpen(true)}>
              {offer?.canStartTrial ? `Try Pro free for ${offer.trialDays} days` : "Upgrade to Pro"}
            </button>
          </section>
        ) : null}

        {offer?.onTrial ? (
          <section className="bio-studio__upgrade">
            <div>
              <h3>
                {offer.trialCancelled
                  ? `Your Gitcha Pro trial ends on ${formatDay(offer.trialEndsOn)}`
                  : offer.keepingPro
                    ? `Gitcha Pro continues on ${formatDay(offer.trialEndsOn)}`
                    : trialLeftLine(offer.trialEndsOn)}
              </h3>
              <p>
                {offer.trialCancelled
                  ? "You cancelled, so your card will not be charged. Your bio goes back to the Standard plan after that day."
                  : offer.keepingPro
                    ? "Your card is set. Nothing changes on your bio when the trial ends."
                    : `Keep Pro to hold on to everything below after ${formatDay(offer.trialEndsOn)}.`}
              </p>
            </div>
            {offer.keepingPro || offer.trialCancelled ? null : (
              <button type="button" className="bio-studio__primary" onClick={() => setPlanOpen(true)}>
                Keep Pro
              </button>
            )}
          </section>
        ) : null}

        {isPro ? (
          <section className="bio-studio__progress" aria-label="Bio progress">
            <div className="bio-studio__progress-bar" aria-hidden="true">
              {Array.from({ length: progress.total }, (_, index) => (
                <span key={index} className={index < progress.done ? "is-on" : ""} />
              ))}
            </div>
            <p>
              {progress.next ? (
                <>
                  <strong>
                    Your bio: {progress.done} of {progress.total} done.
                  </strong>{" "}
                  Next: {midSentence(progress.next.label)}. {progress.next.why}
                </>
              ) : (
                <strong>Your bio is complete.</strong>
              )}
            </p>
            {!offer?.onTrial ? (
              <p className="bio-studio__plan">
                You have Gitcha Pro. Your bio has the request form, testimonials and&nbsp;links.
              </p>
            ) : null}
          </section>
        ) : null}

        <div className="bio-studio__panel">
          <DetailsRow userDetails={userDetails} onEdit={onEditDetails} roster={roster} missing={isPro ? missing : []} />

          {isPro || canUpgrade ? (
            <fieldset className="bio-studio__sections" disabled={locked}>
              <Row title="Photo and logo" className="bio-studio__card">
                <div className="bio-studio__images">
                  <div className="bio-studio__image">
                    <span className="bio-studio__avatar">
                      {shows(draft.Img) ? <img src={draft.Img} alt="" onError={markBroken(draft.Img)} /> : <span>Photo</span>}
                    </span>
                    <label className="bio-studio__secondary">
                      {shows(draft.Img) ? "Change photo" : "Add photo"}
                      <input type="file" accept="image/*" onChange={pickImage(false)} hidden />
                    </label>
                  </div>
                  <div className="bio-studio__image">
                    <span className="bio-studio__logo">
                      {shows(draft.logoUrl) ? (
                        <img src={draft.logoUrl} alt="" onError={markBroken(draft.logoUrl)} />
                      ) : (
                        <span>Logo</span>
                      )}
                    </span>
                    <label className="bio-studio__secondary">
                      {shows(draft.logoUrl) ? "Change logo" : "Add logo"}
                      <input type="file" accept="image/*" onChange={pickImage(true)} hidden />
                    </label>
                  </div>
                </div>
              </Row>

              <Row title="About you">
                <Field
                  label="Bio"
                  hint={`${draft.Bio.length.toLocaleString("en-US")} / ${BIO_LIMIT.toLocaleString("en-US")}`}
                  error={errors.Bio}
                >
                  <textarea
                    rows={5}
                    value={draft.Bio}
                    onChange={set("Bio")}
                    placeholder="Where you work, who you help and what you are known for."
                  />
                </Field>
              </Row>

              <Row
                title="Sections on your page"
                note="Drag a section by its handle to set the order buyers see them in. A section with nothing in it stays off your page."
              >
                <SortableBlocks
                  order={draft.bioLayout.order}
                  labels={SECTION_LABELS}
                  disabled={locked}
                  onChange={(order) => setLayout({ order })}
                  renderBlock={(key, grip) => (
                    <div className="bio-studio__block-inner">
                      <div className="bio-studio__block-head">
                        {grip}
                        <div>
                          <h4>{SECTION_LABELS[key]}</h4>
                          <p className="bio-studio__block-status">{sectionStatus(key)}</p>
                        </div>
                      </div>
                      {key === "testimonials" ? (
                        <div className="bio-studio__block-body">
                          {draft.testimonials.map((quote, index) => (
                            <div className="bio-studio__quote" key={index}>
                              <textarea
                                rows={3}
                                value={quote}
                                onChange={(event) => setTestimonial(index, event.target.value)}
                                placeholder="Paste what a client said about working with you."
                                aria-label={`Testimonial ${index + 1}`}
                              />
                              {draft.testimonials.length > 1 ? (
                                <button type="button" className="bio-studio__link" onClick={() => removeTestimonial(index)}>
                                  Remove
                                </button>
                              ) : null}
                            </div>
                          ))}
                          {draft.testimonials.length < MAX_TESTIMONIALS ? (
                            <button
                              type="button"
                              className="bio-studio__link"
                              onClick={() => setDraft({ ...draft, testimonials: [...draft.testimonials, ""] })}
                            >
                              Add another testimonial
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                      {key === "links" ? (
                        <div className="bio-studio__block-body">
                          <div className="bio-studio__grid">
                            <Field label="Website" error={errors.websiteUrl}>
                              <input value={draft.websiteUrl} onChange={set("websiteUrl")} placeholder="yourbrokerage.com" />
                            </Field>
                            <Field label="Facebook" error={errors.facebookUrl}>
                              <input value={draft.facebookUrl} onChange={set("facebookUrl")} placeholder="facebook.com/yourpage" />
                            </Field>
                            <Field label="Instagram" error={errors.instagramUrl}>
                              <input value={draft.instagramUrl} onChange={set("instagramUrl")} placeholder="instagram.com/you" />
                            </Field>
                            <Field label="YouTube video" error={errors.youtubeUrl}>
                              <input value={draft.youtubeUrl} onChange={set("youtubeUrl")} placeholder="youtube.com/watch?v=..." />
                            </Field>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                />
              </Row>
            </fieldset>
          ) : (
            <Row title="Your agent page">
              <p className="bio-studio__note">
                Your agent page shows your details above and the buyers you are working&nbsp;with.
              </p>
            </Row>
          )}
        </div>

        {isPro && dirty ? (
          <div className="bio-studio__savebar">
            <span>You have unsaved changes.</span>
            <div>
              <button type="button" className="bio-studio__link" onClick={() => setDraft(draftFrom(userDetails))}>
                Discard
              </button>
              <button
                type="button"
                className="bio-studio__primary"
                disabled={!canSave}
                onClick={() => save.mutate(payloadFrom(userDetails, draft))}
              >
                {save.isPending ? "Saving" : "Save changes"}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <aside className="bio-studio__preview">
        <div className="bio-studio__preview-head">
          <span>Preview of your Gitcha.com page</span>
          {!isPro && canUpgrade ? (
            <UnderlineTabs
              label="Preview plan"
              tabs={[
                { key: "free", label: "Free" },
                { key: "pro", label: "With Pro" },
              ]}
              value={previewPro ? "pro" : "free"}
              onChange={(next) => setPreviewPro(next === "pro")}
            />
          ) : null}
        </div>
        <ScaledPreview>
          <AgentBioHeader agent={agent} isGitchaWebsite proBadge onDirectMessage={() => {}} />
          <AgentBioSections agent={agent} layout={draft.bioLayout} onOpen={() => {}} showMissingLinks />
          <BioLegalLinks agent={agent} />
        </ScaledPreview>
      </aside>

      <ProfileImageCropper
        userId={userDetails?.UserId}
        image={cropper?.image}
        show={Boolean(cropper)}
        isSquare={Boolean(cropper?.isSquare)}
        onHide={() => setCropper(null)}
        updateImageAfterCrop={(url) => {
          setDraft((prev) => ({ ...prev, [cropper?.isSquare ? "logoUrl" : "Img"]: url }));
          setCropper(null);
        }}
      />
      {isBlsAgent ? (
        <PremiumPlanModal
          userDetails={userDetails}
          updateReloadParam={updateReloadParam}
          show={planOpen}
          onHide={() => setPlanOpen(false)}
          showPlanModal={() => setPlanOpen(true)}
        />
      ) : null}
    </div>
  );
};

export default BioStudio;
